/**
 * Stripe Client Configuration
 * Handles Stripe.js initialization and checkout/portal helpers
 */

import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { getSupabase } from './supabase';
import { formatPriceFromCents } from './currency';
import { readFunctionError } from './billingErrors';

// Stripe publishable key from environment
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// Singleton Stripe instance
let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Get or create Stripe instance
 */
export function getStripe(): Promise<Stripe | null> {
  if (!stripePublishableKey) {
    console.warn('Stripe publishable key not configured');
    return Promise.resolve(null);
  }

  if (!stripePromise) {
    stripePromise = loadStripe(stripePublishableKey);
  }

  return stripePromise;
}

/**
 * Check if Stripe is configured
 */
export function isStripeConfigured(): boolean {
  return !!stripePublishableKey && stripePublishableKey !== 'pk_test_placeholder';
}

// Pricing tiers configuration
export const PRICING_TIERS = {
  earlyAdopter: {
    id: 'early_adopter',
    name: 'Early Adopter',
    yearlyPrice: 6900, // 69.00 EUR
    monthlyPrice: 700, // 7.00 EUR (the UI sells Early Adopter yearly only)
    maxUnits: 5,
    totalSlots: 50, // real total — get_early_adopter_status returns the live count
    features: ['all_features', 'priority_support', 'early_adopter_badge'],
    isLimited: true,
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    yearlyPrice: 9900, // 99.00 EUR
    monthlyPrice: 999, // 9.99 EUR
    maxUnits: 3, // 1-3 jedinice
    features: ['all_features'],
    isLimited: false,
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    yearlyPrice: 14900, // 149.00 EUR
    monthlyPrice: 1499, // 14.99 EUR
    maxUnits: 6, // 4-6 jedinica
    features: ['all_features', 'priority_support'],
    isLimited: false,
  },
  business: {
    id: 'business',
    name: 'Business',
    yearlyPrice: 24900, // 249.00 EUR
    monthlyPrice: 2499, // 24.99 EUR
    maxUnits: 10, // 7-10 jedinica
    features: ['all_features', 'priority_support', 'api_access'],
    isLimited: false,
  },
} as const;

export type PricingTierId = keyof typeof PRICING_TIERS;

export const CURRENCY = 'eur';

/**
 * Find the smallest tier that covers the given number of units.
 * Returns null if units exceeds all tier limits (must contact sales).
 */
export function getTierForUnits(units: number): PricingTierId | null {
  if (units <= PRICING_TIERS.starter.maxUnits) return 'starter';
  if (units <= PRICING_TIERS.pro.maxUnits) return 'pro';
  if (units <= PRICING_TIERS.business.maxUnits) return 'business';
  return null;
}

/**
 * Calculate subscription price for a given tier and interval.
 * Tier-based pricing: each tier has a fixed price covering up to maxUnits.
 */
export function calculatePrice(
  units: number,
  interval: 'month' | 'year' = 'year'
): {
  tierId: PricingTierId;
  tierName: string;
  maxUnits: number;
  totalPrice: number;
  totalPriceFormatted: string;
  monthlyEquivalent: number;
  monthlyEquivalentFormatted: string;
  savings: number;
  savingsFormatted: string;
} {
  const tierId = getTierForUnits(units) ?? 'business';
  const tier = PRICING_TIERS[tierId];

  const totalPrice = interval === 'year' ? tier.yearlyPrice : tier.monthlyPrice;
  const monthlyEquivalent = interval === 'year' ? Math.round(totalPrice / 12) : totalPrice;

  // Savings = (monthly × 12) − yearly
  const savings = tier.monthlyPrice * 12 - tier.yearlyPrice;

  return {
    tierId,
    tierName: tier.name,
    maxUnits: tier.maxUnits,
    totalPrice,
    totalPriceFormatted: formatPriceFromCents(totalPrice),
    monthlyEquivalent,
    monthlyEquivalentFormatted: formatPriceFromCents(monthlyEquivalent),
    savings,
    savingsFormatted: formatPriceFromCents(savings),
  };
}

/**
 * Format price in cents to display string
 */
export { formatPriceFromCents as formatPrice } from './currency';

/**
 * Create checkout session / change the plan via the create-checkout Edge Function.
 *
 * Request: { tier, interval, preview?, action?, successUrl, cancelUrl }.
 * The server decides from the host's Stripe state (the browser never sends
 * a price, currency or e-mail):
 *   - no live subscription       → { url } (Stripe Checkout)
 *   - upgrade (now, prorated)    → { updated, tier_id, amount_charged }
 *                                  or { requires_action, hosted_invoice_url } (3DS)
 *   - downgrade / year→month     → { scheduled, effective_at (ISO), tier_id, interval }
 *   - action 'cancel_change'     → { change_cancelled: true }
 *   - preview: true              → { preview: { effective, amount_due_now, …,
 *                                    proration_date, checkout (no live sub) } }
 *     Confirm a "now" change with the preview's proration_date so the charge
 *     equals the previewed amount.
 *   - business errors (HTTP 200) → { error_code, error } (EA_SOLD_OUT,
 *     PAYMENT_ISSUE, TOO_MANY_UNITS {active_units, max_units},
 *     UPGRADE_KEEP_INTERVAL, SAME_PLAN, ACCOUNT_PENDING_DELETION)
 */
export interface ChangePreview {
  effective: 'now' | 'period_end';
  /** Charged today, in cents (0 for period_end). */
  amountDueNow: number;
  currency: string;
  /** Price of the new plan per interval, in cents. */
  newPrice: number;
  /** ISO date: next renewal (now) / when the change starts (period_end). */
  periodEnd: string | null;
  /** The change turns automatic renewal back on. */
  clearsCancellation: boolean;
  /** No live subscription: confirming opens Stripe Checkout. */
  checkout: boolean;
  /** Send back unchanged when confirming (unix seconds) — same proration as previewed. */
  prorationDate: number | null;
  /** The change replaces a scheduled (pending) change. */
  cancelsPendingChange: boolean;
}

export type CheckoutResult =
  | { kind: 'redirect'; url: string }
  | { kind: 'updated'; tierId: string | null; amountCharged: number | null }
  | { kind: 'requires_action'; hostedInvoiceUrl: string | null }
  | { kind: 'scheduled'; effectiveAt: string | null; tierId: string | null; interval: 'month' | 'year' | null }
  | { kind: 'change_cancelled' }
  | { kind: 'preview'; preview: ChangePreview }
  | {
      kind: 'error';
      code: string;
      message: string | null;
      activeUnits?: number;
      maxUnits?: number;
      /** TOO_MANY_UNITS: what the server would do once the units fit. */
      preview?: PartialPreview;
      /** PAYMENT_PENDING / PAYMENT_ISSUE: Stripe's page for the open invoice. */
      hostedInvoiceUrl?: string | null;
      /** REACTIVATE_FIRST / TOO_MANY_UNITS: when the cancelling subscription ends (ISO). */
      currentPeriodEnd?: string | null;
      /** TOO_MANY_UNITS while renewal is off: renewal must be turned on first. */
      reactivateFirst?: boolean;
    };

/**
 * The preview the server sends along with TOO_MANY_UNITS — amount_due_now can
 * be null (not computable before the units are switched off).
 */
export interface PartialPreview {
  effective: 'now' | 'period_end';
  amountDueNow: number | null;
  newPrice: number | null;
  periodEnd: string | null;
  clearsCancellation: boolean;
  cancelsPendingChange: boolean;
  checkout: boolean;
}

export interface CheckoutParams {
  tier: PricingTierId;
  interval: 'month' | 'year';
  preview?: boolean;
  action?: 'cancel_change';
  /** From the preview — so the confirmed charge equals the previewed one. */
  proration_date?: number;
  successUrl: string;
  cancelUrl: string;
}

/** effective_at may be ISO (new contract) or unix seconds (old) → ISO. */
function toIso(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value < 1e12 ? value * 1000 : value).toISOString();
  }
  if (typeof value === 'string' && value) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Preview fields of an error body (nested `preview` or top-level). */
function partialPreviewOf(d: Record<string, unknown>): PartialPreview | undefined {
  const src = (d.preview && typeof d.preview === 'object' ? d.preview : d) as Record<string, unknown>;
  if (src.effective !== 'now' && src.effective !== 'period_end') return undefined;
  return {
    effective: src.effective,
    amountDueNow: num(src.amount_due_now),
    newPrice: num(src.new_price),
    periodEnd: toIso(src.period_end),
    clearsCancellation: src.clears_cancellation === true,
    cancelsPendingChange: src.cancels_pending_change === true,
    checkout: src.checkout === true,
  };
}

function errorResult(d: Record<string, unknown>, fallbackCode = 'SERVER'): Extract<CheckoutResult, { kind: 'error' }> {
  return {
    kind: 'error',
    code: typeof d.error_code === 'string' ? d.error_code : fallbackCode,
    message: typeof d.error === 'string' ? d.error : null,
    activeUnits: num(d.active_units) ?? undefined,
    maxUnits: num(d.max_units) ?? undefined,
    preview: partialPreviewOf(d),
    hostedInvoiceUrl: typeof d.hosted_invoice_url === 'string' && d.hosted_invoice_url ? d.hosted_invoice_url : null,
    currentPeriodEnd: typeof d.current_period_end === 'string' && d.current_period_end ? d.current_period_end : null,
    ...(d.reactivate_first === true ? { reactivateFirst: true } : {}),
  };
}

/** Map the create-checkout response body to a CheckoutResult. */
export function parseCheckoutResponse(data: unknown): CheckoutResult {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  if (typeof d.error_code === 'string' || typeof d.error === 'string') {
    return errorResult(d);
  }
  if (d.preview && typeof d.preview === 'object') {
    const p = d.preview as Record<string, unknown>;
    return {
      kind: 'preview',
      preview: {
        effective: p.effective === 'period_end' ? 'period_end' : 'now',
        amountDueNow: num(p.amount_due_now) ?? 0,
        currency: typeof p.currency === 'string' ? p.currency : 'eur',
        newPrice: num(p.new_price) ?? 0,
        periodEnd: toIso(p.period_end),
        clearsCancellation: p.clears_cancellation === true,
        checkout: p.checkout === true,
        prorationDate: num(p.proration_date),
        cancelsPendingChange: p.cancels_pending_change === true,
      },
    };
  }
  if (d.change_cancelled === true) return { kind: 'change_cancelled' };
  if (d.requires_action === true) {
    return {
      kind: 'requires_action',
      hostedInvoiceUrl: typeof d.hosted_invoice_url === 'string' && d.hosted_invoice_url ? d.hosted_invoice_url : null,
    };
  }
  if (d.scheduled === true) {
    return {
      kind: 'scheduled',
      effectiveAt: toIso(d.effective_at),
      tierId: typeof d.tier_id === 'string' ? d.tier_id : null,
      interval: d.interval === 'month' || d.interval === 'year' ? d.interval : null,
    };
  }
  if (d.updated === true) {
    return {
      kind: 'updated',
      tierId: typeof d.tier_id === 'string' ? d.tier_id : null,
      amountCharged: num(d.amount_charged),
    };
  }
  if (typeof d.url === 'string' && d.url) return { kind: 'redirect', url: d.url };
  return { kind: 'error', code: 'SERVER', message: null };
}

/** How long a plan change may take before the host gets a way out. */
export const CHECKOUT_TIMEOUT_MS = 45_000;

const TIMED_OUT = Symbol('timeout');

export async function createCheckoutSession(
  params: CheckoutParams,
  timeoutMs: number = CHECKOUT_TIMEOUT_MS,
): Promise<CheckoutResult> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    const call = getSupabase().functions.invoke('create-checkout', {
      body: params,
      ...(controller ? { signal: controller.signal } : {}),
    });
    const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
      timer = setTimeout(() => {
        controller?.abort();
        resolve(TIMED_OUT);
      }, timeoutMs);
    });
    const outcome = await Promise.race([call, timeout]);
    // TIMEOUT ≠ "failed": the change may have gone through — the UI says so.
    if (outcome === TIMED_OUT) return { kind: 'error', code: 'TIMEOUT', message: null };
    const { data, error } = outcome;

    if (error) {
      const info = await readFunctionError(data, error);
      console.error('Checkout error:', info ?? error);
      return errorResult(info?.body ?? {}, info?.code ?? 'SERVER');
    }

    return parseCheckoutResponse(data);
  } catch (err) {
    console.error('Checkout error:', err);
    return { kind: 'error', code: controller?.signal.aborted ? 'TIMEOUT' : 'NETWORK', message: null };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Get the Stripe Customer Portal URL (card, invoices, cancel at period end).
 * NO_CUSTOMER = the host has never paid through Stripe.
 */
export async function getCustomerPortalUrl(params: {
  returnUrl: string;
}): Promise<{ url: string } | { error: string; code: string }> {
  try {
    const { data, error } = await getSupabase().functions.invoke('customer-portal', {
      body: params,
    });

    const info = await readFunctionError(data, error);
    if (info) {
      console.error('Portal error:', info);
      return { error: info.message ?? info.code ?? 'SERVER', code: info.code ?? 'SERVER' };
    }

    const url = (data as { url?: unknown } | null)?.url;
    if (typeof url !== 'string' || !url) return { error: 'No portal URL', code: 'SERVER' };
    return { url };
  } catch (err) {
    console.error('Portal error:', err);
    return { error: 'network', code: 'NETWORK' };
  }
}

/**
 * Ask the server to read this host's Stripe state right now (instead of
 * waiting for the webhook) — used after returning from Checkout.
 */
export async function syncMyBilling(userId: string): Promise<boolean> {
  try {
    const { data, error } = await getSupabase().functions.invoke('stripe-sync', {
      body: { userId },
    });
    const info = await readFunctionError(data, error);
    if (info) {
      console.warn('stripe-sync failed:', info);
      return false;
    }
    return true;
  } catch (err) {
    console.warn('stripe-sync failed:', err);
    return false;
  }
}

/**
 * Get billing info from database
 */
export async function getBillingInfo(userId: string) {
  const { data, error } = await getSupabase().rpc('get_user_billing_info', {
    p_user_id: userId,
  });

  if (error) {
    console.error('Error fetching billing info:', error);
    return null;
  }

  return data?.[0] || null;
}

/**
 * Check if user can add more units
 */
export async function checkCanAddUnit(userId: string) {
  const { data, error } = await getSupabase().rpc('can_add_unit', {
    p_user_id: userId,
  });

  if (error) {
    console.error('Error checking unit limit:', error);
    return {
      allowed: false,
      currentUnits: 0,
      maxUnits: 0,
      needsUpgrade: true,
      subscriptionStatus: 'error',
    };
  }

  const result = data?.[0];
  return {
    allowed: result?.allowed ?? false,
    currentUnits: result?.current_units ?? 0,
    maxUnits: result?.max_units ?? 0,
    needsUpgrade: result?.needs_upgrade ?? true,
    subscriptionStatus: result?.subscription_status ?? 'unknown',
  };
}

/**
 * Check if user can add more properties (tier-based limit).
 * Mirrors checkCanAddUnit — same plan caps both.
 */
export async function checkCanAddProperty(userId: string) {
  const { data, error } = await getSupabase().rpc('can_add_property', {
    p_user_id: userId,
  });

  if (error) {
    console.error('Error checking property limit:', error);
    return {
      allowed: false,
      currentProperties: 0,
      maxProperties: 0,
      needsUpgrade: true,
      subscriptionStatus: 'error',
    };
  }

  const result = data?.[0];
  return {
    allowed: result?.allowed ?? false,
    currentProperties: result?.current_properties ?? 0,
    maxProperties: result?.max_properties ?? 0,
    needsUpgrade: result?.needs_upgrade ?? true,
    subscriptionStatus: result?.subscription_status ?? 'unknown',
  };
}

/**
 * Get billing history
 */
export async function getBillingHistory(userId: string) {
  const { data, error } = await getSupabase()
    .from('billing_history')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(20);

  if (error) {
    console.error('Error fetching billing history:', error);
    return [];
  }

  return data || [];
}
